import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels } from 'wsm-common-data';
import { Panel } from 'react-bootstrap';
import { trackEvent } from 'ddc-track-event';
import Facet from './Facet';
import FacetTitle from './FacetTitle';
import FacetUserRange from './user-range/FacetUserRange';
import FacetGeoLocation from './geo-location/FacetGeoLocation';
import { transformUserRangeData } from '../utilities/facet-user-range';

const FacetGroup = ({ facet, selectedValues }) => {
	const {
		type,
		label,
		id: groupId,
		multiselect = undefined,
		values,
		collapsed
	} = facet;
	const facetType = multiselect ? 'checkbox' : 'radio';
	const { widgetName, windowId } = useRequestData();
	const [isCollapsed, setCollapsed] = useState(collapsed);
	const labels = useLabels();
	const geoLocationLabel = labels.get('DISTANCE');
	let facetTitle = label;

	// State derived from getAppliedFilters that represents currently selected facets
	const [userSelections, setUserSelections] = useState(selectedValues);

	useEffect(() => {
		setUserSelections(selectedValues);
	}, [selectedValues]);

	const handleChange = (event, facetId) => {
		const targetValue = event.target.value;
		const newSelectedIds = [];

		if (facetType === 'checkbox') {
			selectedValues.map(selected => newSelectedIds.push(selected.value));
			// Remove selected value from array if already included
			const selectedIndex = newSelectedIds.indexOf(targetValue);
			if (selectedIndex !== -1) {
				newSelectedIds.splice(selectedIndex, 1);
			} else {
				// Add facet id to list of selected ids
				newSelectedIds.push(targetValue);
			}
			// Don't push anything to updatedSelections when the selection is ALL
		} else if (targetValue !== 'ALL') {
			newSelectedIds.push(targetValue);
		}

		window.DDC.InvData.setFilterSelections({
			filterName: facetId,
			filterValue: targetValue,
			filterValues: newSelectedIds,
			isChip: false,
			isSelected: newSelectedIds.includes(targetValue),
			isSlider: false,
			element: event.target
		});

		setUserSelections(
			values.filter(selection => newSelectedIds.includes(selection.value))
		);

		if (newSelectedIds.includes(targetValue)) {
			trackEvent(widgetName, windowId, {
				action: 'applied',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet added'
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'removed',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet removed'
			});
		}
	};

	/**
	 * Creates a list of Facet components for each FacetGroup
	 *
	 * @event handleChange
	 * @returns {Facet[] || FacetUserRange || null} Facet components
	 */

	const getFacets = () => {
		if (values.length) {
			if (type === 'USER_RANGE') {
				const [facetValues, noPriceObj] = transformUserRangeData(
					facet,
					!!multiselect
				);
				if (facetValues) {
					return (
						<FacetUserRange
							facetValues={facetValues}
							selectedValues={selectedValues}
							noPriceObj={noPriceObj}
							facetId={facet.id}
							isMultiselect={!!multiselect}
						/>
					);
				} else {
					return null;
				}
			}

			if (type === 'GEO') {
				facetTitle = geoLocationLabel;
				return <FacetGeoLocation />;
			}

			return (
				<ol className="list-unstyled line-height-condensed">
					{values.map(item => {
						// values = DDC.InvData.facets[this facet group].values
						const {
							label: facetLabel,
							value: facetValue,
							count: facetCount
						} = item;
						let selected = !!userSelections.find(
							selection => selection.value === facetValue
						);
						if (!userSelections.length && facetValue === 'ALL') {
							selected = true;
						}
						return (
							<Facet
								facetLabel={facetLabel}
								facetType={facetType}
								facetValue={facetValue}
								facetCount={facetCount}
								groupId={groupId}
								isSelected={selected}
								isDisabled={false}
								onChange={handleChange}
								key={facetLabel}
							/>
						);
					})}
				</ol>
			);
		} else {
			return null;
		}
	};

	const [isInteracted, setInteracted] = useState(false);
	const handleCollapse = () => {
		setCollapsed(!isCollapsed);
		setInteracted(true);

		if (!isCollapsed) {
			trackEvent(widgetName, windowId, {
				action: 'collapsed',
				element: `${groupId} accordion`,
				result: 'Accordion collapsed'
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'expanded',
				element: `${groupId} accordion`,
				result: 'Accordion expanded'
			});
		}
	};
	const expandIcon = isCollapsed ? 'ddc-icon-expand' : 'ddc-icon-collapse';

	let facets = null;
	// Display request facets only when the panel is open
	// or user has previously toggled the panel
	if (!isCollapsed || isInteracted) {
		facets = getFacets();

		if (!facets) {
			return null;
		}
	}

	return (
		<Panel
			className="facet-list-group"
			id={groupId}
			onToggle={handleCollapse}
			expanded={!isCollapsed}
			data-facet-group={groupId}
			role="tablist"
		>
			<Panel.Heading>
				<Panel.Title toggle>
					<FacetTitle {...{ facetTitle, expandIcon }} />
				</Panel.Title>
			</Panel.Heading>
			<Panel.Body collapsible>{facets}</Panel.Body>
		</Panel>
	);
};

FacetGroup.propTypes = {
	facet: PropTypes.shape({
		type: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		isCurrency: PropTypes.bool,
		multiselect: PropTypes.bool,
		values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
		collapsed: PropTypes.bool.isRequired
	}).isRequired,
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default FacetGroup;
